import type { ReactNode } from "react";
import { ClerkProvider } from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { shadcn } from "@clerk/themes";
import { Router as WouterRouter, useLocation } from "wouter";

export const clerkBasePath = import.meta.env.BASE_URL.replace(/\/$/, "");
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

function stripBase(path: string): string {
  return clerkBasePath && path.startsWith(clerkBasePath)
    ? path.slice(clerkBasePath.length) || "/"
    : path;
}

function ClerkProviderWithRouter({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  const clerkPubKey = publishableKeyFromHost(
    window.location.hostname,
    import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  );

  if (!clerkPubKey) {
    throw new Error("Clerk authentication is not configured for this app.");
  }

  const clerkAppearance = {
    theme: shadcn,
    cssLayerName: "clerk",
    options: {
      logoPlacement: "inside" as const,
      logoLinkUrl: clerkBasePath || "/",
      logoImageUrl: `${window.location.origin}${clerkBasePath}/logo.svg`,
    },
    variables: {
      colorPrimary: "#0f766e",
      colorForeground: "#f8fafc",
      colorMutedForeground: "#cbd5e1",
      colorDanger: "#f87171",
      colorBackground: "#0f172a",
      colorInput: "#111827",
      colorInputForeground: "#f8fafc",
      colorNeutral: "#334155",
      fontFamily: "Inter, sans-serif",
      borderRadius: "0.75rem",
    },
    elements: {
      rootBox: "w-full flex justify-center",
      cardBox: "bg-slate-900 rounded-2xl w-[440px] max-w-full overflow-hidden border border-slate-700 shadow-2xl",
      card: "!shadow-none !border-0 !bg-transparent !rounded-none",
      footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
      headerTitle: "text-white",
      headerSubtitle: "text-slate-300",
      socialButtonsBlockButtonText: "text-slate-100",
      formFieldLabel: "text-slate-200",
      footerActionLink: "text-teal-300",
      footerActionText: "text-slate-300",
      dividerText: "text-slate-400",
      identityPreviewEditButton: "text-teal-300",
      formFieldSuccessText: "text-emerald-300",
      alertText: "text-red-300",
      logoBox: "h-10",
      logoImage: "max-h-10",
      socialButtonsBlockButton: "border-slate-700 bg-slate-800 hover:bg-slate-700",
      formButtonPrimary: "bg-teal-700 hover:bg-teal-600 text-white",
      formFieldInput: "border-slate-700 bg-slate-950 text-white placeholder:text-slate-400",
      footerAction: "text-slate-300",
      dividerLine: "bg-slate-700",
      alert: "border-red-800 bg-red-950/50",
      otpCodeFieldInput: "border-slate-700 bg-slate-950 text-white",
      formFieldRow: "text-slate-200",
      main: "text-slate-200",
    },
  };

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${clerkBasePath}/sign-in`}
      signUpUrl={`${clerkBasePath}/sign-up`}
      localization={{
        signIn: {
          start: {
            title: "Sign in",
            subtitle: "Sign in to access your account",
          },
        },
        signUp: {
          start: {
            title: "Create your account",
            subtitle: "Create your account to access your approved canvas",
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      {children}
    </ClerkProvider>
  );
}

export function BrowserClerkProvider({ children }: { children: ReactNode }) {
  return (
    <WouterRouter base={clerkBasePath}>
      <ClerkProviderWithRouter>{children}</ClerkProviderWithRouter>
    </WouterRouter>
  );
}
